import {
  Integer,
  ThemeIconKey,
  Box,
  Clickable,
  HStack,
  Icon,
  Show,
  Space,
  Text,
} from 'platform/foundation';
import {cva} from 'platform/shared';

import {MouseEvent, ReactNode} from 'react';

import {suffixTestId, TestIdProps} from 'shared/web';

import {Actions} from '../../Actions/Actions';
import {Badge} from '../../Badge/Badge';
import {Separator} from '../../Separator/Separator';
import {Tooltip} from '../../Tooltip/Tooltip';
import {MenuAction, MenuBadge} from '../types';

interface MenuItemProps extends TestIdProps {
  id: string;
  label: string;
  hasSeparator?: boolean;
  leftIcon?: ThemeIconKey;
  rightIcon?: ThemeIconKey;
  actions?: MenuAction[];
  isDisabled?: boolean;
  isActive?: boolean;
  depth?: Integer;
  isDark?: boolean;
  isGroup?: boolean;
  badge?: MenuBadge;
  tooltip?: ReactNode;
  shouldTruncateLongText?: boolean;
  onClick: () => void;
}

const menuItemEffectsVariants = cva(
  'flex min-h-9 items-center rounded-[var(--radius-small)] px-2',
  {
    variants: {
      isActive: {
        true: '',
        false: '',
      },
      isDisabled: {
        true: '',
        false: '',
      },
      isDark: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        isActive: false,
        isDisabled: false,
        isDark: false,
        class: 'hover:bg-[var(--color-palettes-neutral-20-100)]',
      },
      {
        isActive: false,
        isDisabled: false,
        isDark: true,
        class: 'hover:bg-[var(--color-palettes-neutral-500-100)]',
      },
      {
        isActive: true,
        isDark: false,
        class: 'bg-[var(--color-palettes-blue-20-100)]',
      },
      {
        isActive: true,
        isDark: true,
        class: 'bg-[var(--color-palettes-blue-70-100)]',
      },
    ],
  }
);

export function MenuItem(props: MenuItemProps) {
  const depth: Integer = props.depth || 0;

  const actions: MenuAction[] | undefined = props.actions?.map((action) =>
    action.type === 'iconButton'
      ? {
          ...action,
          onClick: (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            action.onClick(event);
          },
        }
      : {
          ...action,
          menuItems: action.menuItems.map((item) => ({
            ...item,
            onClick: (event: MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              item.onClick?.(event);
            },
          })),
        }
  );

  return (
    <>
      <Clickable onClick={props.onClick} isDisabled={props.isDisabled} role="button">
        <div
          className={menuItemEffectsVariants({
            isDark: Boolean(props.isDark),
            isDisabled: Boolean(props.isDisabled),
            isActive: Boolean(!props.isGroup && props.isActive),
          })}
          data-testid={props['data-testid']}
        >
          <Box
            opacity={props.isDisabled ? 0.5 : 1}
            flex={1}
            overflow={props.shouldTruncateLongText ? 'hidden' : undefined}
          >
            <HStack align="center">
              <Box overflow={props.shouldTruncateLongText ? 'hidden' : undefined}>
                <Tooltip label={props.tooltip}>
                  <HStack align="center">
                    <Space horizontal={(depth * 4) as Integer} />
                    <Show when={!props.leftIcon}>
                      <Space horizontal={2} />
                    </Show>
                    <Show when={props.leftIcon}>
                      <Icon
                        size={4}
                        value={props.leftIcon}
                        color={props.isDark ? 'text.white' : 'text.primary'}
                      />
                      <Space horizontal={2} />
                    </Show>
                    <Text
                      alternative={props.isGroup && props.isActive}
                      size="small"
                      color={props.isDark ? 'white' : 'primary'}
                      overflowWrap="anywhere"
                      noWrap={props.shouldTruncateLongText}
                      data-testid={props['data-testid']}
                    >
                      {props.label}
                    </Text>
                    <Show when={props.badge}>
                      {(badge) => (
                        <>
                          <Space horizontal={2} />
                          <Show when={badge.value}>
                            {(value) => (
                              <Badge
                                size="small"
                                variant="bold"
                                colorScheme={badge.colorScheme}
                                value={value}
                              />
                            )}
                          </Show>
                          <Show when={badge.icon}>
                            {(icon) => (
                              <Badge
                                size="small"
                                variant="bold"
                                colorScheme={badge.colorScheme}
                                icon={icon}
                              />
                            )}
                          </Show>
                        </>
                      )}
                    </Show>
                  </HStack>
                </Tooltip>
              </Box>
              <Space fillAvailable />
              <Show when={props.actions || props.rightIcon}>
                <HStack align="center" spacing={2}>
                  <Actions
                    size="small"
                    actions={actions}
                    data-testid={suffixTestId(`actions`, props)}
                  />
                  <Show when={props.rightIcon}>
                    <Icon
                      size={4}
                      value={props.rightIcon}
                      color={
                        props.isDark // eslint-disable-line no-nested-ternary -- Pre-existing violation
                          ? 'text.white'
                          : props.isGroup
                            ? 'text.primary'
                            : 'general.accent'
                      }
                    />
                  </Show>
                </HStack>
              </Show>
            </HStack>
          </Box>
        </div>
      </Clickable>
      <Show when={props.hasSeparator}>
        <Box opacity={props.isDark ? 0.2 : 1}>
          <Separator spacing={1} />
        </Box>
      </Show>
    </>
  );
}
